import { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { CustomButton, CustomLoadingButton } from "src/design";
import {
  PaymentError,
  resolvePaymentErrorKey,
} from "src/business/payment/paymentErrors";
import { useCancelPayment } from "src/data/payment/queries/payment";
import { colorTextError } from "src/design/tokens";

interface CancelPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  restaurantId: string;
  paymentId: string;
  /** Raised on a 409 — the payment was resolved while the page was open. */
  onConflict: () => void;
}

/**
 * Cancelling is a status transition, not a deletion: the payment stays in the
 * history as CANCELLED, and the restaurateur is free to declare a corrected
 * one. Behind a confirmation because nothing brings it back.
 */
export const CancelPaymentDialog = ({
  open,
  onClose,
  restaurantId,
  paymentId,
  onConflict,
}: CancelPaymentDialogProps) => {
  const { t } = useTranslation();
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const cancelPayment = useCancelPayment(restaurantId);

  const handleConfirm = () => {
    setErrorKey(null);
    cancelPayment.mutate(paymentId, {
      onSuccess: onClose,
      onError: (error) => {
        setErrorKey(resolvePaymentErrorKey(error));
        if (error instanceof PaymentError && error.status === 409) {
          onConflict();
        }
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={() => !cancelPayment.isPending && onClose()}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {t("payments.cancel.title")}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>{t("payments.cancel.body")}</DialogContentText>
        {errorKey && (
          <Typography variant="body2" sx={{ mt: 1, color: colorTextError }}>
            {t(errorKey)}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <CustomButton
          color="inverted"
          onClick={onClose}
          disabled={cancelPayment.isPending}
        >
          {t("payments.cancel.dismiss")}
        </CustomButton>
        <CustomLoadingButton
          loading={cancelPayment.isPending}
          disabled={cancelPayment.isPending}
          onSubmit={handleConfirm}
          label={t("payments.cancel.confirm")}
          color="error"
          fullwidth={false}
        />
      </DialogActions>
    </Dialog>
  );
};
